import React, { useEffect, useState } from 'react';
import { Activity, Archive, CheckCircle, Clock, FileText, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../../lib/adminApi';
import FiveStarAnalyticsSection from './FiveStarAnalyticsSection';

const StatBlock = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl border bg-white p-4 ${accent || 'border-slate-200'}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <Icon className="w-7 h-7 text-slate-500" />
      <div>
        <div className="text-2xl font-bold text-slate-900">{value ?? '—'}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  </div>
);

export const AnalyticsHubTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setReloading(true);
    try {
      const [a, u] = await Promise.all([adminApi.analytics(), adminApi.activeUsers()]);
      setAnalytics(a);
      setActiveUsers(u);
    } catch (err) {
      toast.error(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-slate-500 py-6">Loading analytics…</div>;
  if (!analytics) return <div className="text-sm text-slate-500 py-6">No analytics available.</div>;

  return (
    <div data-testid="tab-analytics" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900">Platform Analytics</div>
          <div className="text-xs text-slate-500">Snapshot of users, tacticals, and activity.</div>
        </div>
        <button
          type="button"
          onClick={() => load(false)}
          disabled={reloading}
          data-testid="analytics-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${reloading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBlock icon={Users} label="Online Now" value={activeUsers?.online_now} accent="border-emerald-200" />
        <StatBlock icon={Clock} label="Active (15m)" value={activeUsers?.active_15min} />
        <StatBlock icon={Activity} label="Active (1h)" value={activeUsers?.active_1hour} />
        <StatBlock icon={Users} label="Total Users" value={analytics.total_users} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBlock icon={FileText} label="Active Tacticals" value={analytics.active_tacticals} accent="border-blue-200" />
        <StatBlock icon={Archive} label="Archived" value={analytics.archived_tacticals} />
        <StatBlock icon={CheckCircle} label="Users w/ Tactical" value={analytics.users_with_active_tactical} />
        <StatBlock icon={Users} label="Manager Links" value={analytics.approved_manager_relationships} />
      </div>

      {analytics.scorecard_health && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-bold text-slate-900 mb-3">Scorecard Health</div>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            <div className="bg-emerald-500" style={{ width: `${analytics.scorecard_health.green_pct || 0}%` }} />
            <div className="bg-amber-400" style={{ width: `${analytics.scorecard_health.yellow_pct || 0}%` }} />
            <div className="bg-rose-500" style={{ width: `${analytics.scorecard_health.red_pct || 0}%` }} />
          </div>
          <div className="flex gap-4 mt-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Green {analytics.scorecard_health.green_pct || 0}%</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Yellow {analytics.scorecard_health.yellow_pct || 0}%</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Red {analytics.scorecard_health.red_pct || 0}%</span>
          </div>
        </div>
      )}

      <FiveStarAnalyticsSection />
    </div>
  );
};
